import { useMemo } from 'react'
import { ThemePreview } from './ThemePreview'
import { CreateThemeButton } from './CreateThemeButton'
import { useDocumentPageThemes } from '@/services/queries/document_themes'
import { useDocumentState } from '@/state'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { useListDocumentPageThemeUserDefaults } from '@/services/queries/document_themes_user_defaults'
import { Spinner } from '@/components/ui/spinner'
import { validate as isUUID } from 'uuid'

export const OperationThemePreviews = () => {
  const {
    data: { isReadOnly },
  } = useIsReadOnly()

  const currentDocumentPageId = useDocumentState(
    (state) => state.currentPageInView,
  )
  const { isLoading: isLoadingThemes, data: themes } = useDocumentPageThemes()
  const { isLoading: isLoadingDefaultThemes, data: userDefaultThemes } =
    useListDocumentPageThemeUserDefaults()

  const isLoading = isLoadingThemes || isLoadingDefaultThemes

  const pageThemes = useMemo(
    () => (themes || []).filter((t) => t.theme_type === 'operation'),
    [themes],
  )

  const orgThemes = useMemo(
    () => pageThemes.filter((t) => t.visibility_scope !== 'global'),
    [pageThemes],
  )

  const desiredOrder = useMemo(
    () => ['Two Image', 'One image', 'Checkbox', 'Blank'],
    [],
  )

  const globalThemes = useMemo(
    () =>
      pageThemes
        .filter((t) => t.visibility_scope === 'global')
        .sort(
          (a, b) => desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name),
        ),
    [pageThemes, desiredOrder],
  )

  return (
    <div
      className="border-l h-full shadow-inner py-4 px-4 overflow-y-auto flex flex-col"
      data-testid="operation-templates-list"
    >
      <h3 className="text-left text-sm font-semibold">Document Templates</h3>
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <div className="flex flex-col items-start space-y-3 mt-4">
            {!isReadOnly &&
              currentDocumentPageId &&
              isUUID(currentDocumentPageId) && (
                <>
                  <CreateThemeButton />
                  <p className="text-xs text-gray-400">
                    Click on an operation page and save the format as a new
                    template.
                  </p>
                </>
              )}
          </div>
          <div className="my-4 py-4 border-t">
            <h3 className="text-left text-sm font-semibold mb-4">
              Saved Templates
            </h3>
            <div className="flex flex-col space-y-4">
              {orgThemes.map((tmpl) => (
                <ThemePreview
                  key={tmpl.id}
                  documentPageId={currentDocumentPageId}
                  documentPageThemeId={tmpl.id}
                  themeTemplateAttributes={tmpl.template_attributes}
                  themeName={tmpl.name}
                  isReadOnly={isReadOnly}
                  isDefault={Boolean(
                    userDefaultThemes?.find(
                      (t) =>
                        t.document_page_theme === tmpl.id &&
                        t.theme_type === 'operation',
                    ),
                  )}
                />
              ))}
            </div>
          </div>
          <div className="my-4 py-4 border-t">
            <h3 className="text-left text-sm font-semibold mb-4">
              Quarter20 Templates
            </h3>
            <div className="flex flex-col space-y-4">
              {globalThemes.map((tmpl) => (
                <ThemePreview
                  key={tmpl.id}
                  documentPageId={currentDocumentPageId}
                  documentPageThemeId={tmpl.id}
                  themeTemplateAttributes={tmpl.template_attributes}
                  themeName={tmpl.name}
                  isReadOnly={isReadOnly}
                  isBuiltIn
                  isDefault={Boolean(
                    userDefaultThemes?.find(
                      (t) =>
                        t.document_page_theme === tmpl.id &&
                        t.theme_type === 'operation',
                    ),
                  )}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
